import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    ViewChildren,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { AnnualReportBusinessModel } from '@modeso/aumico-lib-annualreport-fe';
import {
    EMPTY_ARRAY_LENGTH,
    ModalComponent,
    ModalData,
    ModalTitleIcons,
    RemoveItemsFromMappingWarningDialogActions,
} from '@modeso/aumico-lib-core-fe';
import {
    AccountMappingAccountItem,
    AccountMappingTemplateGroupItem,
    AccountType,
    FinancialStatementTypes,
    IAccount,
    IAccountMappingHeader,
    IAccountMappingSubFLI,
    IAccountMappingTemplate,
    IAccountMappingTemplateGroup,
    IAccountMappingTemplateGroupItemFLI,
    IAccountMappingTemplateGroupItemSubtotal,
    ItemType,
    SortedMappingItem,
} from '@modeso/types__aumico-lib-annualreport-be';
import { INameId } from '@modeso/types__aumico-lib-core-be';
import { FinancialLineItemCashFlowType, FinancialLineItemType } from '@modeso/types__aumico-lib-settings-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextService } from 'angular-i18next';
import Debug from 'debug';
import { Observable, filter, tap } from 'rxjs';
import { localizationKeys } from '../../../constants/localizationKeys';
import { AS_FIRST_ELEMENT_OPTION_ID } from '../../../constants/subtotal-form.constant';
import { MenuAction } from '../../../enums/menu-action.enum';
import { GroupAccountMappingFormHelper } from '../../../helpers/group-account-mapping.helper';
import { MenuItemActionExecutionHelper as MenuActionExecutionHelper } from '../../../helpers/menu-item-action-execution.helper';
import { IAssignAccountEvent } from '../../../interfaces/IAssignAccountEvent.interface';
import { IGroupAccountMappingComponentDelegate } from '../../../interfaces/IGroupAccountMappingComponentDelegate.interface';
import { IAddSubtotalFormDialogData } from '../../../interfaces/add-subtotal-form-dialog-data.interface';
import { IAddSubtotalFormDialogResult } from '../../../interfaces/add-subtotal-form-dialog-result.interface';
import { IFliFormDialogData } from '../../../interfaces/fli-form-dialog-data.interface';
import { IFliFormDialogResult } from '../../../interfaces/fli-form-dialog-result.interface';
import { IGroupFormDialogResult } from '../../../interfaces/group-form-dialog-result.interface';
import { Item, Parent } from '../../../interfaces/menu-item.class';
import { IUpdateAccount } from '../../../interfaces/update-account.interface';
import { AccountMappingEngine } from '../../../mappers/account-mapping-engin';
import { FliInEditModeService } from '../../../services/fli-in-edit-mode.service';
import { isAccount, isFli, isGroup, isHeaderGroup, isSubTotal, isSubfli } from '../../../type-guards/type-guards';
import { INameIdWithSortedItems } from '../../../types/name-id-with-sorted-items.type';
import { DuplicateAccountFormDialogComponent } from '../../duplicate-account-form-dialog/duplicate-account-form.dialog';
import { FliFormDialogComponent } from '../fli-form/fli-form.dialog';
import { AddSubtotalFormDialogComponent } from '../mapping-add-subtotal-form/add-subtotal-form.dialog';
import { GroupFormDialogComponent } from '../mapping-group-form/group-form.dialog';
import { SubFliFormDialog } from '../sub-fli-form/sub-fli-form.dialog';

type Unpacked<T> = T extends EventEmitter<infer U> ? U : never;

const debug = Debug('aumico:aumico-lib-templates-fe:TemplateGroupAccountMappingComponent');

const CASH_FLOW_TYPES_LOCALIZATION_KEYS = [
    'aumico_templates_group_account_mapping_cash_flow_type_none',
    'aumico_templates_group_account_mapping_cash_flow_type_operations',
    'aumico_templates_group_account_mapping_cash_flow_type_investing',
    'aumico_templates_group_account_mapping_cash_flow_type_financing',
    'aumico_templates_group_account_mapping_cash_flow_type_networking_capital',
];

const FLI_TYPES_LOCALIZATION_KEYS = [
    'aumico_templates_group_account_mapping_fli_type_assets',
    'aumico_templates_group_account_mapping_fli_type_liabilities',
    'aumico_templates_group_account_mapping_fli_type_income',
    'aumico_templates_group_account_mapping_fli_type_expenses',
];

const ONE = 1;

/**
 * Data passed to the component will be mutated
 */
@UntilDestroy()
@Component({
    // tslint:disable-next-line: component-selector
    selector: 'templates-group-account-mapping',
    templateUrl: 'group-account-mapping.component.html',
    styleUrls: ['group-account-mapping.component.scss'],
    providers: [FliInEditModeService],
})
export class GroupAccountMappingComponent implements OnInit, OnDestroy {
    @ViewChildren('accountList', { read: ElementRef })
    accountList!: QueryList<ElementRef>;

    @ViewChildren('headerList', { read: ElementRef })
    headerList!: QueryList<ElementRef>;
    private itemsWithNewlyAddedAccounts: (IAccountMappingSubFLI | IAccountMappingTemplateGroupItemFLI)[];

    readonly HIGHEST_LEVEL = 0;
    readonly FIRST_LEVEL = 1;
    levelsIndentations: number[] = [];
    flisLevelsIndentations: number[] = [];
    _currentLanguage = 'de-ch';
    elementToScrollTo: string;
    menuTriggerMap: Map<number, MatMenuTrigger> = new Map();
    expandedPanels: Set<string> = new Set<string>();

    @Input() hasPreviousData: boolean;
    @Input() groupIndex: number;

    @Input() businessModel: string;
    @Input() reportLocale: string;

    // Input for current language
    @Input() set currentLanguage(value: string) {
        if (value != null) {
            this._currentLanguage = value;
        }
    }

    @Input()
    languages = ['de-ch', 'en-us'];

    @Input() defaultLanguage = 'de-ch';

    @Input() parent: GroupAccountMappingComponent;
    // tslint:disable-next-line: variable-name
    private _templateGroup: IAccountMappingTemplateGroup;
    @Input()
    public get templateGroup(): IAccountMappingTemplateGroup {
        return this.level === 0 ? this.delegate.templateGroup : this._templateGroup;
    }
    public set templateGroup(value: IAccountMappingTemplateGroup) {
        this._templateGroup = value;
    }

    @Input() templateGroupsList: SortedMappingItem[] | undefined;
    @Input() engine: AccountMappingEngine | undefined;
    @Input() cashAndCashFli: string | undefined;
    @Input() profitOfTheYearFliName: string | undefined;
    @Input() errorInProfitFli: boolean | undefined;
    @Input() isAutomaticTransferOfAnnualResultEnabled: boolean | undefined;

    @Input() level = this.HIGHEST_LEVEL;

    @Input() touched: boolean = false;

    collapseButtonClicked: boolean | undefined;
    @Input() set isCollapseButtonClicked(value: boolean | undefined) {
        this.collapseButtonClicked = value;
        if (value) {
            this.collapseAllPanels();
        }
    }

    public get collapseAll() {
        return this.delegate.collapseAll;
    }
    public get expandAll() {
        return this.delegate.expandAll;
    }

    @Input() parentGroup?: IAccountMappingTemplateGroup;

    get accountMappingMode() {
        return this.delegate.accountMappingMode;
    }

    @Input()
    delegate: IGroupAccountMappingComponentDelegate;

    @Input() type: FinancialStatementTypes;

    @Output() addOrUpdateAccountInActiveReport = new EventEmitter<IUpdateAccount>();

    @Output() fliOrGroupDeleted = new EventEmitter<
        IAccountMappingTemplateGroup | IAccountMappingTemplateGroupItemFLI
    >();

    public financialLineItemsOptions: INameId[] = [];

    @Input() isCashFlowTypeForFliUsed: boolean | undefined;

    @Input() disableEditingPreviousYearValue = false;

    @Input() supportOperation = false;

    private financialStatementArrayIndex: number;
    private menuActionExecutionHelper: MenuActionExecutionHelper;
    public savedHeadersNames = new Set<string>([]);

    constructor(
        private readonly dialog: MatDialog,
        private readonly localizedTexts: I18NextService,
        private readonly fliEditModeService: FliInEditModeService,
    ) { }

    ngOnDestroy(): void {
        if (this.itemsWithNewlyAddedAccounts != undefined) {
            this.itemsWithNewlyAddedAccounts.forEach((item) => {
                item.accounts = item.accounts.filter((account: IAccount) => account.accountNumber && account.name);
            });
        }
    }

    ngOnInit(): void {
        this.prepareLevelsIndentations();
        this.setIndexOfArrayInFS();
        this.menuActionExecutionHelper = new MenuActionExecutionHelper(this.delegate, this.dialog);
        this.savedHeadersNames = this.engine.getSavedHeadersNames(this.templateGroup);
    }

    private setIndexOfArrayInFS(): void {
        if (this.type !== FinancialStatementTypes.BALANCE && this.type !== FinancialStatementTypes.INCOME) {
            throw new Error('unhandled financial statement type');
        }
        this.financialStatementArrayIndex = this.type === FinancialStatementTypes.BALANCE ? 0 : 1;
    }

    private prepareLevelsIndentations(): void {
        for (let incrementor = this.FIRST_LEVEL; incrementor < this.level; incrementor++) {
            this.levelsIndentations.push(incrementor);
        }

        for (let incrementor = this.FIRST_LEVEL; incrementor < this.level + 1; incrementor++) {
            this.flisLevelsIndentations.push(incrementor);
        }
    }

    public mapSelectedAccounts(
        item: IAccountMappingTemplateGroupItemFLI | IAccountMappingSubFLI,
        parentFli: IAccountMappingTemplateGroupItemFLI,
        parentGroup: IAccountMappingTemplateGroup,
        event: MouseEvent,
    ): void {
        if (this.accountMappingMode) {
            event.preventDefault();
            event.stopPropagation();
            this.mapSelectedAccountsInComponent(item, parentGroup, parentFli);
        }
    }

    private mapSelectedAccountsInComponent(
        item: IAccountMappingTemplateGroupItemFLI | IAccountMappingSubFLI,
        parentGroup: IAccountMappingTemplateGroup,
        parent?: IAccountMappingTemplateGroupItemFLI | null,
    ): void {
        if (item.name === this.profitOfTheYearFliName &&
            this.isAutomaticTransferOfAnnualResultEnabled) {
            // Don't allow mapping accounts to profit of the year FLI if automatic transfer of annual result is enabled
            return;
        }

        if (this.level === this.HIGHEST_LEVEL) {
            // just do this on the highest level of the component
            this.delegate.assignSelectedAccounts({
                item,
                parentGroup,
                parent,
            } as IAssignAccountEvent);
        } else {
            if (this.parent) {
                this.parent.mapSelectedAccountsInComponent(item, parentGroup, parent);
            }
        }
    }

    public removeAccountFrom(
        item: IAccountMappingTemplateGroupItemFLI | IAccountMappingSubFLI,
        accountIndex: number,
    ): void {
        if (this.disableEditingPreviousYearValue) {
            this.openRemovalWarningDialog(
                'aumico_templates_mapping_remove_account_will_change_previous_year_values_warning',
            )
                .pipe(untilDestroyed(this))
                .subscribe((dialogResult) => {
                    if (dialogResult === RemoveItemsFromMappingWarningDialogActions.REMOVE) {
                        this.handleAccountRemoval(item, accountIndex);
                    }
                });

            return;
        }

        this.handleAccountRemoval(item, accountIndex);
    }

    private handleAccountRemoval(
        item: IAccountMappingTemplateGroupItemFLI | IAccountMappingSubFLI,
        accountIndex: number,
    ): void {
        const accountTopGroupName = this.delegate.templateGroup.name;

        if (isFli(item) && this.level === 0) {
            this.delegate.removeAccountFromFLI(item, accountIndex, accountTopGroupName);
            return;
        } else if (isFli(item)) {
            this.delegate.removeAccountFromFLI(item, accountIndex, accountTopGroupName);
            return;
        } else if (isSubfli(item)) {
            this.delegate.removeAccountFromSubFLI(item, accountIndex, accountTopGroupName);
        }
    }

    private getAccountType(): AccountType {
        if (this.type === FinancialStatementTypes.BALANCE) {
            return AccountType.BALANCE;
        } else {
            return AccountType.INCOME;
        }
    }

    public addNewSubFLI(): void {
        const dialogRef = this.dialog.open(SubFliFormDialog, {
            width: '30%',
            data: {
                allFlis: this.delegate.allFlis.reverse(),
                language: this.currentLanguage,
                showOfThatItemCheckBox: this.businessModel === AnnualReportBusinessModel.HGB,
            },
        });

        const subscription = dialogRef.componentInstance.saveEvent.subscribe({
            next: (event: Unpacked<SubFliFormDialog['saveEvent']>) => {
                subscription.unsubscribe();
                this.delegate.addSubFli({
                    parent: event.parent,
                    subFli: event.subFli,
                });
                dialogRef.close();
            },
        });
    }

    public addNewFLI(): void {
        const cashFlowTypesIds = Object.values(FinancialLineItemCashFlowType)
            .filter((value) => typeof value === 'number')
            .sort(this.predicateCashFlowTypes);

        const cashFlowTypeOptions = cashFlowTypesIds.map((cashflowTypeId, index) => {
            return {
                id: cashflowTypeId.toString(),
                name: this.localizedTexts.t(CASH_FLOW_TYPES_LOCALIZATION_KEYS[index]) as string,
            };
        }) as INameId[];

        const fliTypesIds = Object.values(FinancialLineItemType);
        const fliTypeOptions = fliTypesIds.map((fliTypeId, index) => {
            return {
                id: fliTypeId,
                name: this.localizedTexts.t(FLI_TYPES_LOCALIZATION_KEYS[index]) as string,
            };
        }) as INameId[];

        const existingFliNames = this.delegate.getAllFlisInMappingTemplate().map((fli) => fli.name);

        const groupOptions = GroupAccountMappingFormHelper.getAllGroupsOptions([this.delegate.templateGroup]);

        const fliFormInputData: IFliFormDialogData = {
            cashFlowTypeOptions,
            fliTypeOptions,
            existingFliNames,
            groupOptions,
            isCashFlowTypeForFliUsed: this.isCashFlowTypeForFliUsed,
        };
        const dialogRef = this.dialog.open(FliFormDialogComponent, {
            width: '30%',
            data: fliFormInputData,
        });

        dialogRef
            .afterClosed()
            .pipe(
                filter((result) => result != undefined),
                untilDestroyed(this),
            )
            .subscribe((result: IFliFormDialogResult) => {
                const parentGroup = GroupAccountMappingFormHelper.findGroupByName(result.parentGroup.id, [
                    this.delegate.templateGroup,
                ]);

                const timestamp = Date.now();
                const timestampString = timestamp.toString();
                result.fliName = result.fliCaption + '-' + timestampString;

                const createdFli = GroupAccountMappingFormHelper.constructFli(result);

                this.delegate.addFli({
                    parent: parentGroup,
                    fli: createdFli,
                });
            });
    }

    public changeItemCaption(item: IAccountMappingSubFLI, newCaption: string): void {
        if (newCaption?.trim().length > EMPTY_ARRAY_LENGTH) {
            this.delegate.renameItem(item, newCaption);
        }
    }

    public addSubtotal(item: IAccountMappingTemplateGroup): void {
        const groupOptions = this.prepareGroupOptionsForSubTotal(item);

        const flisAndGroupsPositionOptions: INameId[] = (item.sortedItems as SortedMappingItem[])
            .filter((item) => isGroup(item as SortedMappingItem) || isFli(item as SortedMappingItem))
            .map((item) => {
                return {
                    id: item.name,
                    name: isFli(item as SortedMappingItem)
                        ? (item as IAccountMappingTemplateGroupItemFLI).caption
                        : (item as IAccountMappingTemplateGroup).title,
                };
            });

        const fliFormInputData: IAddSubtotalFormDialogData = {
            groupOptions,
            flisAndGroupsPositionOptions,
            allItemsInSelectedScope: item.sortedItems as SortedMappingItem[],
        };

        const dialogRef = this.dialog.open(AddSubtotalFormDialogComponent, {
            width: '30%',
            data: fliFormInputData,
        });

        dialogRef
            .afterClosed()
            .pipe(
                filter((result) => result != undefined),
                untilDestroyed(this),
            )
            .subscribe((result: IAddSubtotalFormDialogResult) => {
                const parentGroup = GroupAccountMappingFormHelper.findGroupByName(item.name, [
                    this.delegate.templateGroup,
                ]);

                const scopedGroup = GroupAccountMappingFormHelper.findGroupByName(result.scopedGroup.id, [
                    this.delegate.templateGroup,
                ]);

                const createdSubtotal = GroupAccountMappingFormHelper.constructAddSubtotal(result, scopedGroup);
                this.delegate.addSubtotalAfterIndex({
                    parent: parentGroup,
                    subtotal: createdSubtotal,
                    index: result.positionBeforeSubtotalIndex,
                    placeSubtotalAsFirstItem: result.positionBeforeSubtotal.id === AS_FIRST_ELEMENT_OPTION_ID,
                });
            });
    }

    public editSubtotal(item: Item, parentGroup: IAccountMappingTemplateGroup): void {
        const groupOptions = this.prepareGroupOptionsForSubTotal(parentGroup);

        let flisAndGroupsPositionOptions: INameId[] = (parentGroup.sortedItems as SortedMappingItem[])
            .filter((item) => isGroup(item as SortedMappingItem) || isFli(item as SortedMappingItem))
            .map((item) => {
                return {
                    id: item.name,
                    name: isFli(item as SortedMappingItem)
                        ? (item as IAccountMappingTemplateGroupItemFLI).caption
                        : (item as IAccountMappingTemplateGroup).title,
                };
            });

        const scopedGroup = (item as IAccountMappingTemplateGroupItemSubtotal).scope;

        const getAllGroupsNamesToGroupsMap = this.delegate.getAllGroupsNamesToGroupsMap();
        const scopedGroupObject = getAllGroupsNamesToGroupsMap.get(scopedGroup.name);

        const index = parentGroup.sortedItems.findIndex((sortedItem) => {
            return sortedItem.name === item.name && sortedItem.type === ItemType.SUB_TOTAL;
        });
        const itemBeforeSubtotal = parentGroup.sortedItems[index - 1];

        let selectedPositionBeforeSubtotal: INameId | undefined;
        if (itemBeforeSubtotal != null) {
            if (isGroup(itemBeforeSubtotal as SortedMappingItem)) {
                selectedPositionBeforeSubtotal = flisAndGroupsPositionOptions.find(
                    (option) =>
                        option.name === (itemBeforeSubtotal as IAccountMappingTemplateGroup).title &&
                        option.id === itemBeforeSubtotal.name,
                );
            } else {
                selectedPositionBeforeSubtotal = flisAndGroupsPositionOptions.find(
                    (option) =>
                        option.name === (itemBeforeSubtotal as IAccountMappingTemplateGroupItemFLI).caption &&
                        option.id === itemBeforeSubtotal.name,
                );
            }
        } else {
            selectedPositionBeforeSubtotal = {
                id: 'as-first-element',
                name: this.localizedTexts.t('aumico_templates_subtotal_select_position_as_first_element') as string,
            };
        }

        const fliFormInputData: IAddSubtotalFormDialogData = {
            groupOptions,
            flisAndGroupsPositionOptions,
            allItemsInSelectedScope: parentGroup.sortedItems as SortedMappingItem[],
            subTotalSaveData: {
                subtotalName: (item as IAccountMappingTemplateGroupItemSubtotal).caption,
                positionBeforeSubtotal: selectedPositionBeforeSubtotal,
                positionBeforeSubtotalIndex: index - 1,
                scopedGroup: {
                    id: scopedGroup.name,
                    name: scopedGroupObject.title,
                    sortedItems: scopedGroupObject.sortedItems,
                    isDirectParent: scopedGroupObject.name == parentGroup.name,
                },
            },
        };

        const dialogRef = this.dialog.open(AddSubtotalFormDialogComponent, {
            width: '30%',
            data: fliFormInputData,
        });

        dialogRef
            .afterClosed()
            .pipe(
                filter((result) => result != undefined),
                untilDestroyed(this),
            )
            .subscribe((result: IAddSubtotalFormDialogResult) => {
                const scopedGroup = GroupAccountMappingFormHelper.findGroupByName(result.scopedGroup.id, [
                    this.delegate.templateGroup,
                ]);
                const createdSubtotal = GroupAccountMappingFormHelper.constructAddSubtotal(result, scopedGroup);
                this.delegate.updateSubtotal(
                    createdSubtotal,
                    item as IAccountMappingTemplateGroupItemSubtotal,
                    result.positionBeforeSubtotalIndex,
                    index,
                    parentGroup as IAccountMappingTemplateGroup,
                );
            });
    }

    private prepareGroupOptionsForSubTotal(parent: Parent): INameIdWithSortedItems[] {
        const path: IAccountMappingTemplateGroup[] = [];

        GroupAccountMappingFormHelper.getItemParentGroupAndGrandparentsGroups(
            this.delegate.templateGroup,
            parent,
            path,
        );

        // The path is from target to root, reverse it to get root to target
        const groupOptions = path.reverse().map((group: IAccountMappingTemplateGroup): INameIdWithSortedItems => {
            return {
                id: group.name,
                name: group.title,
                sortedItems: group.sortedItems,
                isDirectParent: parent.name === group.name ? true : false,
            };
        });

        return groupOptions;
    }

    public editFli(item: Item, parentGroup: Parent): void {
        if (isSubTotal(item)) {
            this.editSubtotal(item, parentGroup as IAccountMappingTemplateGroup);
            return;
        }

        if (!isFli(item) || !isGroup(parentGroup)) return;

        this.fliEditModeService.enterFliEditMode(item.name);

        const cashFlowTypesIds = Object.values(FinancialLineItemCashFlowType)
            .filter((value) => typeof value === 'number')
            .sort(this.predicateCashFlowTypes);

        const cashFlowTypeOptions = cashFlowTypesIds.map((cashflowTypeId, index) => {
            return {
                id: cashflowTypeId.toString(),
                name: this.localizedTexts.t(CASH_FLOW_TYPES_LOCALIZATION_KEYS[index]) as string,
            };
        }) as INameId[];

        const fliTypesIds = Object.values(FinancialLineItemType);

        const fliTypeOptions = fliTypesIds.map((fliTypeId, index) => {
            return {
                id: fliTypeId,
                name: this.localizedTexts.t(FLI_TYPES_LOCALIZATION_KEYS[index]) as string,
            };
        }) as INameId[];

        const groupOptions = [];
        groupOptions.push(...GroupAccountMappingFormHelper.getAllGroupsOptions([this.delegate.templateGroup]));

        const existingFliNames = this.delegate.getAllFlisInMappingTemplate().map((fli) => fli.name);

        const fliFormInputData: IFliFormDialogData = {
            cashFlowTypeOptions,
            fliTypeOptions,
            groupOptions,
            existingFliNames,
            fliToEdit: GroupAccountMappingFormHelper.mapAccountMappingFliToFormFli(
                item,
                parentGroup,
                cashFlowTypeOptions,
                fliTypeOptions,
                groupOptions,
            ),
            isCashFlowTypeForFliUsed: this.isCashFlowTypeForFliUsed,
        };
        const dialogRef = this.dialog.open(FliFormDialogComponent, {
            width: '30%',
            data: fliFormInputData,
        });

        dialogRef
            .afterClosed()
            .pipe(
                tap(() => this.fliEditModeService.exitFliEditMode()),
                filter((result) => result != undefined),
                untilDestroyed(this),
            )
            .subscribe((result: IFliFormDialogResult) => {
                const updatedFli = GroupAccountMappingFormHelper.constructFli(result, item);
                const newParentGroup = GroupAccountMappingFormHelper.findGroupByName(result.parentGroup.id, [
                    this.delegate.templateGroup,
                ]);

                if (newParentGroup == null) {
                    throw new Error('Group not found');
                }

                this.delegate.updateFli(updatedFli, item, newParentGroup, parentGroup);
            });
    }

    public changeTemplateGroupTitle(group: IAccountMappingTemplateGroup, newTitle: string): void {
        if (newTitle?.trim().length > EMPTY_ARRAY_LENGTH) {
            this.delegate.renameGroup(group, newTitle);
        }
    }
    public onAddAccountClicked(item: Item): void {
        if (!isFli(item) && !isSubfli(item)) return;

        if (this.itemsWithNewlyAddedAccounts == null) {
            this.itemsWithNewlyAddedAccounts = [item];
        } else {
            this.itemsWithNewlyAddedAccounts.push(item);
        }
        item.accounts.push({} as IAccount);
        this.expandPanel(item.name);
    }

    public onDuplicateAccountClicked(item: Item, parent: Parent, index: number): void {
        const ofThatItemSiblings = this.delegate.getOfThatItemSiblings(parent as IAccountMappingSubFLI);
        const dialogRef = this.dialog.open(DuplicateAccountFormDialogComponent, {
            width: '30%',
            data: ofThatItemSiblings,
        });

        dialogRef
            .afterClosed()
            .pipe(
                filter(
                    (result: IAccountMappingSubFLI[]) => result != undefined && result.length !== EMPTY_ARRAY_LENGTH,
                ),
                untilDestroyed(this),
            )
            .subscribe((ofThatItems: IAccountMappingSubFLI[]) => {
                this.delegate.cloneAccount(item as IAccount, parent as IAccountMappingSubFLI, ofThatItems, index);
            });
    }

    public onAccountSaved(
        item: IAccountMappingSubFLI | IAccountMappingTemplateGroupItemFLI,
        addedOrUpdatedAccount: IAccount,
        oldAccount: IAccount,
        accountIndex: number,
    ): void {
        let accountType = addedOrUpdatedAccount.type;
        if (
            accountType !== AccountType.SPECIAL_ACCOUNT_DELETE_ON_MAPPING &&
            accountType !== AccountType.UNCOVERED_SHORTFALL
        ) {
            accountType = this.getAccountType();
        }
        let accountWithType: IAccount = {
            ...addedOrUpdatedAccount,
            type: accountType,
        };
        if (!addedOrUpdatedAccount.rowNo) {
            accountWithType = {
                ...accountWithType,
                rowNo: this.delegate.getAddedAccountRowNumber(),
            };
        }

        this.delegate.addOrUpdateAccount(oldAccount, accountWithType, item, accountIndex);
        this.addOrUpdateAccountInActiveReport.emit({
            financialStatementTemplateTypeIndex: this.financialStatementArrayIndex,
            updatedAccount: accountWithType,
            oldAccount,
        });
    }

    public onCancelAccountCreation(
        item: IAccountMappingSubFLI | IAccountMappingTemplateGroupItemFLI,
        accountIndex: number,
    ): void {
        item.accounts.splice(accountIndex, 1);
    }
    public onAddOrUpdateActiveReport(modifiedAccount: IUpdateAccount): void {
        this.addOrUpdateAccountInActiveReport.emit(modifiedAccount);
    }

    public onSaveHeaderCaption(caption: string, item: IAccountMappingHeader | IAccountMappingTemplateGroup) {
        if (isHeaderGroup(item)) {
            item.header = caption.trim();
        } else {
            const headerItem = item as IAccountMappingHeader;
            headerItem.caption = caption.trim();
        }
        this.savedHeadersNames.add(item.name);
    }

    public moveGroupItem(
        groupItem: AccountMappingTemplateGroupItem | IAccountMappingSubFLI,
        up: boolean,
        event: Event,
        parentFli?: IAccountMappingTemplateGroupItemFLI,
    ): void {
        event.stopPropagation();
        if (groupItem.type === ItemType.SUB_FLI || groupItem.type === ItemType.OF_THAT_ITEM) {
            const index = parentFli.accounts.findIndex(
                (account) => account.name === groupItem.name && account.type === groupItem.type,
            );
            const replacedIndex = up ? index - 1 : index + 1;
            const temp = parentFli.accounts[replacedIndex];
            parentFli.accounts[replacedIndex] = parentFli.accounts[index];
            parentFli.accounts[index] = temp;
        } else {
            const index = this.templateGroup.sortedItems.findIndex(
                (item) => item.name === groupItem.name && item.type === groupItem.type,
            );
            const replacedIndex = up ? index - 1 : index + 1;
            const temp = this.templateGroup.sortedItems[replacedIndex];
            this.templateGroup.sortedItems[replacedIndex] = this.templateGroup.sortedItems[index];
            this.templateGroup.sortedItems[index] = temp;
        }
        this.delegate.reorderGroupItems();
    }

    public removeItem(item: AccountMappingTemplateGroupItem | AccountMappingAccountItem, event: MouseEvent): void {
        event?.stopPropagation();
        if (item.type === ItemType.FLI) {
            this.openRemovalWarningDialog(localizationKeys.aumico_templates_mapping_remove_item_warning_dialog_text)
                .pipe(untilDestroyed(this))
                .subscribe((dialogResult) => {
                    if (dialogResult === RemoveItemsFromMappingWarningDialogActions.REMOVE) {
                        this.delegate.removeItemFromParentGroup(item, true);

                        this.fliOrGroupDeleted.emit(item);
                    }
                });
            return;
        }

        this.delegate.removeItemFromParentGroup(item as AccountMappingTemplateGroupItem, true);
    }

    public moveGroup(templateGroup: IAccountMappingTemplateGroup, up: boolean, event: Event): void {
        event.stopPropagation();
        const index = this.templateGroupsList.findIndex((group) => group.name === templateGroup.name && isGroup(group));
        const replacedIndex = up ? index - 1 : index + 1;
        if (replacedIndex < EMPTY_ARRAY_LENGTH || replacedIndex > this.templateGroupsList.length - ONE) {
            return;
        }
        const temp = this.templateGroupsList[replacedIndex];
        this.templateGroupsList[replacedIndex] = this.templateGroupsList[index];
        this.templateGroupsList[index] = temp;
        this.delegate.reorderGroupItems();
    }

    public removeGroup(templateGroup: IAccountMappingTemplateGroup, event: MouseEvent | undefined): void {
        event?.stopPropagation();
        this.openRemovalWarningDialog(localizationKeys.aumico_templates_mapping_remove_group_warning_dialog_text)
            .pipe(untilDestroyed(this))
            .subscribe((dialogResult) => {
                if (dialogResult === RemoveItemsFromMappingWarningDialogActions.REMOVE) {
                    templateGroup.items.forEach((item) => {
                        this.delegate.removeItemFromParentGroup(item, true);
                    });
                    this.delegate.removeGroup(templateGroup);
                    this.fliOrGroupDeleted.emit(templateGroup);
                } else {
                    return;
                }
            });
    }
    public onMenuActionClicked(menuAction: MenuAction, item: Item, parent?: Parent, index?: number) {
        switch (menuAction) {
            case MenuAction.ADD_ACCOUNT:
                this.onAddAccountClicked(item);
                break;
            case MenuAction.DUPLICATE_ACCOUNT:
                this.onDuplicateAccountClicked(item, parent, index);
                break;
            case MenuAction.EDIT:
                this.editFli(item, parent);
                break;
            case MenuAction.ADD_GROUP:
                if (isGroup(item)) this.addNewGroup(item);

                break;
            case MenuAction.ADD_SUBTOTAL:
                this.addSubtotal(item as IAccountMappingTemplateGroup);
                break;
            case MenuAction.INSERT_HEADER_ABOVE: {
                const addedItemAbove = this.menuActionExecutionHelper.addMappingItem(
                    item,
                    parent,
                    ItemType.HEADER,
                    'ABOVE',
                );
                this.scrollToElement(this.headerList, `${addedItemAbove.name}${index - 1}`);
                break;
            }
            case MenuAction.INSERT_HEADER_BELOW: {
                const addedItemBelow = this.menuActionExecutionHelper.addMappingItem(
                    item,
                    parent,
                    ItemType.HEADER,
                    'BELOW',
                );
                this.scrollToElement(this.headerList, `${addedItemBelow.name}${index + 1}`);
                break;
            }
            case MenuAction.INSERT_SPACING_ABOVE:
                this.menuActionExecutionHelper.addMappingItem(item, parent, ItemType.EMPTY, 'ABOVE');
                break;
            case MenuAction.INSERT_SPACING_BELOW:
                this.menuActionExecutionHelper.addMappingItem(item, parent, ItemType.EMPTY, 'BELOW');
                break;
            case MenuAction.SWITCH_ACCOUNT:
                if (isAccount(item))
                    this.menuActionExecutionHelper.splitAccount(
                        item,
                        parent as IAccountMappingSubFLI | IAccountMappingTemplateGroupItemFLI,
                        index,
                    );
                break;
            case MenuAction.DISABLE_FLI_NUMBERING:
                if (isFli(item) || isGroup(item) || isSubfli(item) || isSubTotal(item)) {
                    item.disableNumbering = true;
                }
                break;
            case MenuAction.ENABLE_FLI_NUMBERING:
                if (isFli(item) || isGroup(item) || isSubfli(item) || isSubTotal(item)) {
                    item.disableNumbering = false;
                }
                break;
            default:
                break;
        }
    }
    private openRemovalWarningDialog(
        warningText: string,
    ): Observable<RemoveItemsFromMappingWarningDialogActions | undefined> {
        return this.dialog
            .open<ModalComponent, ModalData, RemoveItemsFromMappingWarningDialogActions>(ModalComponent, {
                disableClose: false,
                panelClass: 'custom-modalbox',
                data: {
                    showCloseButton: true,
                    titleIcon: ModalTitleIcons.WARNING,
                    footerButtons: [
                        {
                            localizationKey: 'aumico_templates_mapping_remove_item_warning_dialog_remove_button',
                            action: RemoveItemsFromMappingWarningDialogActions.REMOVE,
                            disabled: false,
                            buttonColor: 'primary',
                        },
                        {
                            localizationKey: 'aumico_templates_mapping_remove_item_warning_dialog_cancel_button',
                            action: RemoveItemsFromMappingWarningDialogActions.CANCEL,
                            buttonColor: 'basic',
                        },
                    ],
                    content: {
                        text: [warningText],
                        icon: 'aumico-pattern-icon-alert-triangle',
                    },
                },
            })
            .afterClosed();
    }

    public isGroupDeletable(group: IAccountMappingTemplateGroup): boolean {
        if (this.type === FinancialStatementTypes.INCOME) {
            return true;
        }
        const mappingTemplate = this.engine?.getTemplateMapping();
        if (
            group.name === mappingTemplate?.equityGroupName ||
            group.name === mappingTemplate?.uncoveredShortfallLiabilitiesFli
        ) {
            return false;
        } else {
            return this.checkIfGroupItemsCanBeDeleted(group, mappingTemplate);
        }
    }

    public isGroupItemDeletable(item: AccountMappingTemplateGroupItem): boolean {
        if (this.type === FinancialStatementTypes.INCOME) {
            return true;
        }
        const mappingTemplate = this.engine?.getTemplateMapping();
        if (
            item.type === ItemType.FLI &&
            (item.name === mappingTemplate?.uncoveredShortfallAssetsFli ||
                item.name === mappingTemplate?.uncoveredShortfallLiabilitiesFli ||
                item.name === mappingTemplate?.profitOfTheYear ||
                item.name === this.cashAndCashFli)
        ) {
            return false;
        }
        return true;
    }

    private checkIfGroupItemsCanBeDeleted(
        templateGroup: IAccountMappingTemplateGroup,
        mappingTemplate: IAccountMappingTemplate,
    ): boolean {
        for (const item of templateGroup.items) {
            const isGroupItemDeletable = this.isGroupItemDeletable(item);
            if (!isGroupItemDeletable) {
                return false;
            }
        }
        for (const group of templateGroup.groups) {
            const nonDeletableFli = this.checkIfGroupsOfGroupItemsCanBeDeleted(group, mappingTemplate);
            if (!nonDeletableFli) {
                return false;
            }
        }
        return true;
    }

    private checkIfGroupsOfGroupItemsCanBeDeleted(
        templateGroup: IAccountMappingTemplateGroup,
        mappingTemplate: IAccountMappingTemplate,
    ): boolean {
        for (const group of templateGroup.groups) {
            const nonDeletableFliInGroup = group.items.find(
                (item) =>
                    item.type === ItemType.FLI &&
                    (item.name === mappingTemplate?.uncoveredShortfallAssetsFli ||
                        item.name === mappingTemplate?.uncoveredShortfallLiabilitiesFli ||
                        item.name === mappingTemplate?.profitOfTheYear ||
                        item.name === this.cashAndCashFli),
            );
            if (nonDeletableFliInGroup != null) {
                return false;
            }
            if (group.groups.length === 0) {
                continue;
            }
            const nonDeletableFli = this.checkIfGroupItemsCanBeDeleted(group, mappingTemplate);
            if (nonDeletableFli != null) {
                return false;
            }
        }
        return true;
    }

    public onFliOrGroupDeleted(deletedItem: IAccountMappingTemplateGroup | IAccountMappingTemplateGroupItemFLI): void {
        this.fliOrGroupDeleted.emit(deletedItem);
    }
    public trackSelectionsBy = (index: number, item: AccountMappingTemplateGroupItem): string => {
        if (isFli(item)) {
            return item.name + item.accounts.length;
        }
        return item.name;
    };
    private scrollToElement(list: QueryList<ElementRef>, elementId: string) {
        setTimeout(() => {
            const element = list.toArray().find((element) => element.nativeElement.id === elementId);
            if (element == undefined) {
                // check dom as action can be triggered from another component due to recursion
                const item = document.getElementById(elementId);
                if (item) {
                    this.scrollInto(item);
                }

                return;
            }
            this.scrollInto(element.nativeElement);
        });
    }
    private scrollInto(nativeElement: HTMLElement): void {
        nativeElement.scrollIntoView({ behavior: 'smooth' });
    }

    public addNewGroup(item?: IAccountMappingTemplateGroup): void {
        const selectedGroup = item ?? this.delegate.templateGroup;

        const groupNames = GroupAccountMappingFormHelper.getAllGroupsOptions([this.delegate.templateGroup]).map(
            (group) => group.name,
        );

        const flis = this.delegate.getAllFlisInMappingTemplate();
        this.prepareFliOptionsFromGroupFlis(flis);
        const dialogRef = this.dialog.open(GroupFormDialogComponent, {
            width: '30%',
            data: {
                financialLineItemsOptions: this.financialLineItemsOptions,
                existingGroupsNames: groupNames,
            },
        });

        dialogRef
            .afterClosed()
            .pipe(
                filter((result) => result != undefined),
                untilDestroyed(this),
            )
            .subscribe((result: IGroupFormDialogResult) => {
                let financialLineItemsToBeAdded: AccountMappingTemplateGroupItem[] = [];
                financialLineItemsToBeAdded =
                    result.financialLineItems != null
                        ? GroupAccountMappingFormHelper.mapFlisNameIdToGroupItem(
                            result.financialLineItems,
                            flis,
                            this.engine,
                        )
                        : [];

                const groupToBeAdded: IAccountMappingTemplateGroup = {
                    name: result.name,
                    title: result.name,
                    totalName: result.totalName,
                    hideTotal: false,
                    groups: [],
                    items: financialLineItemsToBeAdded,
                };

                const sortedItems: SortedMappingItem[] = [];
                financialLineItemsToBeAdded.forEach((item) => {
                    sortedItems.push(item);
                });
                groupToBeAdded.sortedItems = sortedItems;

                for (const fli of financialLineItemsToBeAdded) {
                    this.delegate.removeItemFromParentGroup(fli, false);
                }

                this.delegate.addGroup(selectedGroup, groupToBeAdded);
            });
    }

    private prepareFliOptionsFromGroupFlis(flis: IAccountMappingTemplateGroupItemFLI[]): void {
        this.financialLineItemsOptions = [];
        flis.forEach((financialLineItem) => {
            this.financialLineItemsOptions.unshift({
                id: financialLineItem.name,
                name: financialLineItem.caption,
            } as INameId);
        });
    }

    togglePanel(itemName: string): void {
        if (this.expandedPanels.has(itemName)) {
            // if item exists in expandedPanels Set, then it was expanded before and now collapsed
            this.expandedPanels.delete(itemName);

            return;
        }

        this.expandedPanels.add(itemName);
    }

    private expandPanel(itemName: string): void {
        if (!this.expandedPanels.has(itemName)) {
            this.expandedPanels.add(itemName);
        }
    }

    collapseAllPanels(): void {
        this.expandedPanels.clear();
    }

    private predicateCashFlowTypes = (cashFlowA, cashFlowB) => {
        const map = new Map<FinancialLineItemCashFlowType, number>();

        map.set(FinancialLineItemCashFlowType.NONE, 1);
        map.set(FinancialLineItemCashFlowType.OPERATIONS, 2);
        map.set(FinancialLineItemCashFlowType.INVESTING, 3);
        map.set(FinancialLineItemCashFlowType.FINANCING, 4);
        map.set(FinancialLineItemCashFlowType.NETWORKING_CAPITAL, 5);

        if (map.get(cashFlowA) < map.get(cashFlowB)) {
            return -1;
        }

        if (map.get(cashFlowA) > map.get(cashFlowB)) {
            return 1;
        }

        return 0;
    };

    public drop(event: CdkDragDrop<any[]>, groupItem: AccountMappingTemplateGroupItem | IAccountMappingSubFLI): void {
        const { previousIndex, currentIndex } = event;
        moveItemInArray(groupItem.accounts, previousIndex, currentIndex);
    }
}
